import { useQuery } from '@tanstack/react-query';
import { formatDateShort, formatDateTime } from '~/lib/DateTime';
import { formatCurrency } from '~/lib/CurrencyHelpers';
import Loading from '~/components/Loading';
import { get } from '~/lib/Api';
import {
    endOfDay,
    endOfMonth,
    isToday,
    startOfMonth,
    subMonths,
} from 'date-fns';
import PageHeading from '~/components/PageHeading';
import * as Toast from '~/lib/Toast';
import ErrorMessageBlock from '~/components/ErrorMessageBlock.jsx';
import CopyIcon from '~/components/icons/CopyIcon.jsx';
import { ucfirst } from '~/lib/Utils.js';

function useReferralsQuery(start, end) {
    return useQuery({
        queryKey: ['referrals', start, end],
        queryFn: () =>
            get(
                `/api/referrals?start=${start.toISOString()}&end=${end.toISOString()}`,
            ),
    });
}

const SummaryRow = ({ data }) => {
    return (
        <div className="flex items-start space-x-4 mb-8 max-w-[800px]">
            <div>
                <div className="panel-label">Earnings</div>
                <div className="font-medium text-4xl p-4 panel text-center h-20 flex items-center justify-center">
                    {formatCurrency(data?.earnings || 0)}
                </div>
            </div>
            <div>
                <div className="panel-label">Signups</div>
                <div className="font-medium min-w-20 text-4xl p-4 panel text-center h-20 flex items-center justify-center">
                    {data?.num_signups || 0}
                </div>
            </div>
            <div className="grow">
                <div className="panel-label">Referral Link</div>
                <div className="p-4 panel text-center h-20 flex items-center justify-center">
                    {/* Show a text input that selected everything when they focus and a button to copy the content*/}
                    <div className="flex items-center border rounded-lg grow">
                        <input
                            type="text"
                            value={data?.link || ''}
                            readOnly
                            className="flex-1 border-none"
                            onFocus={e => e.target.select()}
                        />
                        <button
                            type="button"
                            className=""
                            onClick={() => {
                                navigator.clipboard.writeText(data?.link || '');
                                Toast.success('Copied!');
                            }}
                        >
                            <CopyIcon className="w-4 h-4 text-primary-700 mr-2" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ReferralsPage = ({}) => {
    const start = startOfMonth(subMonths(new Date(), 6));
    const end = endOfDay(new Date());
    const { data, isLoading } = useReferralsQuery(start, end);

    return (
        <div>
            <PageHeading
                heading="Referrals"
                className="mb-4 max-w-[800px]"
                rightContent={
                    <div>
                        {formatDateShort(start)} -{' '}
                        {isToday(end) ? 'Today' : formatDateShort(end)}
                    </div>
                }
            />
            {isLoading && <Loading />}
            {!!data?.error && (
                <ErrorMessageBlock className="max-w-2xl">
                    {data?.error}
                </ErrorMessageBlock>
            )}
            {!isLoading && !data?.error && (
                <div>
                    <SummaryRow data={data} />

                    <div className="max-w-[800px]">
                        <table className="w-full">
                            <thead>
                                <tr>
                                    <th className="column-label text-left py-2 px-4">
                                        Business
                                    </th>
                                    <th className="column-label text-left py-2 px-4">
                                        Signup Date
                                    </th>
                                    <th className="column-label text-left py-2 px-4">
                                        Current Plan
                                    </th>
                                    <th className="column-label text-left py-2 px-4">
                                        Earnings
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="panel rounded-lg">
                                {data?.referrals.map(referral => (
                                    <tr key={referral.business_id}>
                                        <td className="py-2 px-4">
                                            {referral.business_name}
                                        </td>
                                        <td className="py-2 px-4">
                                            {formatDateTime(
                                                referral.signup_date,
                                            )}
                                        </td>
                                        <td className="py-2 px-4">
                                            {ucfirst(referral.current_plan)}
                                        </td>
                                        <td className="py-2 px-4">
                                            {referral.earnings > 0
                                                ? formatCurrency(
                                                      referral.earnings,
                                                  )
                                                : '--'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReferralsPage;
